import PropTypes from "prop-types";
import * as React from "react";
import { CartProduct } from "../../domain";
import { MAX_CUSTOMER_TEXT_LENGTH } from "../../domain/influencerService";
import { useDomId } from "../../hooks/useDomId";
import { Alert, Button, Textarea, TextInput, Typography } from "../../themeV2";
import { usd } from "../../util/formatCurrency";
import { inputValidation } from "../../util/inputValidation";
import { countWords } from "../../util/text";
import MessageDialog from "../MessageModal";
import CustomizationCard from "./CustomizationCard";
import css from "./CartProductEditor.module.css";

const addonPrice = (addon) =>
  addon.rawPrice === 0 ? "Free" : usd(addon.rawPrice);

const characterCount = (text, max) =>
  text.length > 0 ? `${text.length} / ${max}` : undefined;

const wordCount = (text, service) =>
  service.maxWordCount
    ? `${countWords(text)} / ${service.maxWordCount} words`
    : undefined;

const CartProductEditor = React.memo(function CartProductEditor({
  cartProduct,
  editor,
  errorRef,
  headingLevel = 3,
}) {
  const {
    sections: { addableServices, hasPersonalization, hasAddons, hasServices },
    addedAddons,
    unaddedAddons,
    draft,
    textFor,
    setField,
    setServiceText,
    setPendingServiceText,
    addAddon,
    removeAddon,
    addService,
    removeService,
    error,
  } = editor;
  const baseId = useDomId("cart-product-editor");
  const Heading = `h${headingLevel}`;
  const [confirm, setConfirm] = React.useState({ open: false });

  const confirmRemoval = ({ title, message, remove }) =>
    setConfirm({
      title,
      message,
      buttonText: "Keep",
      confirmText: "Remove",
      open: true,
      async onConfirm() {
        await remove();
        setConfirm({ open: false });
      },
      onClose: () => setConfirm({ open: false }),
    });

  const removeButton = ({ label, onClick, disabled = false }) => (
    <Button
      variant="text"
      onClick={onClick}
      disabled={disabled}
      aria-label={`Remove ${label}`}
    >
      Remove
    </Button>
  );

  return (
    <div className={css.root}>
      {hasPersonalization && (
        <div className={css.fields}>
          {cartProduct.allowPersonalization && (
            <TextInput
              label="Make it out to"
              value={draft.personalizationTo}
              onValueChange={(value) => setField("personalizationTo", value)}
              maxLength={inputValidation.personalizationTo.maxLength}
              description={characterCount(
                draft.personalizationTo,
                inputValidation.personalizationTo.maxLength,
              )}
            />
          )}

          {cartProduct.allowNotes && (
            <Textarea
              label="Special instructions"
              value={draft.notes}
              onValueChange={(value) => setField("notes", value)}
              maxLength={inputValidation.notes.maxLength}
              description={characterCount(
                draft.notes,
                inputValidation.notes.maxLength,
              )}
            />
          )}
        </div>
      )}

      {(hasServices || hasAddons) && (
        <Typography variant="body-sm" color="variant">
          Add-ons and services are added to your cart right away.
        </Typography>
      )}

      {hasServices && (
        <section className={css.section} aria-labelledby={`${baseId}-services`}>
          <Typography as={Heading} variant="title-sm" id={`${baseId}-services`}>
            Services
          </Typography>

          <ul className={css.cards}>
            {cartProduct.services.map((svc) => (
              <CustomizationCard
                key={svc.cartProductServiceId}
                selected
                title={svc.label}
                price={usd(svc.addonPrice)}
                action={removeButton({
                  label: svc.label,
                  onClick: () =>
                    confirmRemoval({
                      title: "Remove service",
                      message: `Are you sure you want to remove ${svc.label}?`,
                      remove: () => removeService(svc),
                    }),
                })}
              >
                {svc.requiresTextInput && (
                  <Textarea
                    label={`Your text for ${svc.label}`}
                    rows={2}
                    value={textFor(svc)}
                    onValueChange={(value) =>
                      setServiceText(svc.cartProductServiceId, value)
                    }
                    maxLength={MAX_CUSTOMER_TEXT_LENGTH}
                    description={wordCount(textFor(svc), svc)}
                  />
                )}
              </CustomizationCard>
            ))}

            {addableServices.map((svc) => {
              const pending =
                draft.pendingServiceTexts[svc.influencerServiceId] ?? "";

              return (
                <CustomizationCard
                  key={svc.influencerServiceId}
                  title={svc.label}
                  description={svc.description}
                  price={`+${usd(svc.totalPrice)}`}
                  action={
                    <Button
                      variant="tonal"
                      onClick={() => addService(svc)}
                      aria-label={`Add ${svc.label}`}
                    >
                      Add
                    </Button>
                  }
                >
                  {svc.requiresTextInput && (
                    <Textarea
                      label={`Text for ${svc.label}`}
                      rows={2}
                      value={pending}
                      onValueChange={(value) =>
                        setPendingServiceText(svc.influencerServiceId, value)
                      }
                      maxLength={MAX_CUSTOMER_TEXT_LENGTH}
                      description={wordCount(pending, svc)}
                    />
                  )}
                </CustomizationCard>
              );
            })}
          </ul>
        </section>
      )}

      {hasAddons && (
        <section className={css.section} aria-labelledby={`${baseId}-addons`}>
          <Typography as={Heading} variant="title-sm" id={`${baseId}-addons`}>
            Add-ons
          </Typography>

          <ul className={css.cards}>
            {addedAddons.map((addon) => (
              <CustomizationCard
                key={addon.name}
                selected
                icon={addon.icon}
                title={addon.title}
                description={addon.description}
                price={addonPrice(addon)}
                action={removeButton({
                  label: addon.title,
                  disabled: addon.rawPrice === 0,
                  onClick: () =>
                    confirmRemoval({
                      title: "Remove add-on",
                      message: `Are you sure you want to remove the ${addon.title} add-on?`,
                      remove: () => removeAddon(addon),
                    }),
                })}
              />
            ))}

            {unaddedAddons.map((addon) => (
              <CustomizationCard
                key={addon.name}
                icon={addon.icon}
                title={addon.title}
                description={addon.description}
                price={`+${addonPrice(addon)}`}
                action={
                  <Button
                    variant="tonal"
                    onClick={() => addAddon(addon)}
                    aria-label={`Add ${addon.title}`}
                  >
                    Add
                  </Button>
                }
              />
            ))}
          </ul>
        </section>
      )}

      {error && (
        <Alert ref={errorRef} tabIndex={-1} severity="error">
          {error}
        </Alert>
      )}

      <MessageDialog {...confirm} />
    </div>
  );
});

CartProductEditor.propTypes = {
  cartProduct: PropTypes.instanceOf(CartProduct).isRequired,
  editor: PropTypes.shape({
    sections: PropTypes.shape({
      addableServices: PropTypes.array.isRequired,
      hasPersonalization: PropTypes.bool.isRequired,
      hasAddons: PropTypes.bool.isRequired,
      hasServices: PropTypes.bool.isRequired,
    }).isRequired,
    addedAddons: PropTypes.array.isRequired,
    unaddedAddons: PropTypes.array.isRequired,
    draft: PropTypes.shape({
      personalizationTo: PropTypes.string.isRequired,
      notes: PropTypes.string.isRequired,
      pendingServiceTexts: PropTypes.object.isRequired,
    }).isRequired,
    textFor: PropTypes.func.isRequired,
    setField: PropTypes.func.isRequired,
    setServiceText: PropTypes.func.isRequired,
    setPendingServiceText: PropTypes.func.isRequired,
    addAddon: PropTypes.func.isRequired,
    removeAddon: PropTypes.func.isRequired,
    addService: PropTypes.func.isRequired,
    removeService: PropTypes.func.isRequired,
    error: PropTypes.string.isRequired,
  }).isRequired,
  errorRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  headingLevel: PropTypes.oneOf([3, 4]),
};

export default CartProductEditor;
